import React, { useState } from 'react';
import { useListarFuncionarios, useCadastrarUsuario, useAtualizarUsuario, useAlternarStatusUsuario, useListarEstabelecimentos } from '../../hooks/useAdmin';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { TipoUsuario } from '../../types';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import { validateRequired, validateEmail, validateMinLength, combineValidations } from '../../utils/validations';

interface CreateFormData {
  nome: string;
  email: string;
  senha: string;
  tipo: TipoUsuario;
  estabelecimentoId: string;
  comissaoPercentual: string;
}

interface EditFormData {
  id: number;
  nome: string;
  email: string;
  comissaoPercentual: string;
}

const tipoLabels: Record<TipoUsuario, string> = {
  [TipoUsuario.Administrador]: 'Administrador',
  [TipoUsuario.GerenteEstabelecimento]: 'Gerente de Estabelecimento',
  [TipoUsuario.FuncionarioEstabelecimento]: 'Funcionário',
};

const Funcionarios: React.FC = () => {
  usePageTitle('Funcionários');
  const { user: usuarioLogado } = useAuth();
  const isAdmin = usuarioLogado?.tipo === TipoUsuario.Administrador;
  const isGerente = usuarioLogado?.tipo === TipoUsuario.GerenteEstabelecimento;

  const [filtroNome, setFiltroNome] = useState('');
  const [filtroEmail, setFiltroEmail] = useState('');
  const [filtroEstabelecimentoId, setFiltroEstabelecimentoId] = useState('');

  const { data: funcionarios, isLoading } = useListarFuncionarios(
    isAdmin
      ? {
          nome: filtroNome || undefined,
          email: filtroEmail || undefined,
          estabelecimentoId: filtroEstabelecimentoId ? parseInt(filtroEstabelecimentoId) : undefined,
        }
      : undefined
  );

  const { data: estabelecimentos } = useListarEstabelecimentos();
  const { mutate: cadastrar, isPending: cadastrando } = useCadastrarUsuario();
  const { mutate: atualizar, isPending: atualizando } = useAtualizarUsuario();
  const { mutate: alternarStatus } = useAlternarStatusUsuario();

  const [mostrarModalCriar, setMostrarModalCriar] = useState(false);
  const [mostrarModalEditar, setMostrarModalEditar] = useState(false);

  const [createForm, setCreateForm] = useState<CreateFormData>({
    nome: '',
    email: '',
    senha: '',
    tipo: TipoUsuario.FuncionarioEstabelecimento,
    estabelecimentoId: '',
    comissaoPercentual: '',
  });

  const [editForm, setEditForm] = useState<EditFormData>({ id: 0, nome: '', email: '', comissaoPercentual: '' });

  const abrirModalCriar = () => {
    setCreateForm({
      nome: '',
      email: '',
      senha: '',
      tipo: TipoUsuario.FuncionarioEstabelecimento,
      estabelecimentoId: isAdmin ? '' : String(usuarioLogado?.estabelecimentoId ?? ''),
      comissaoPercentual: '',
    });
    setMostrarModalCriar(true);
  };

  const abrirModalEditar = (f: { id: number; nome: string; email: string; comissaoPercentual?: number }) => {
    setEditForm({
      id: f.id,
      nome: f.nome,
      email: f.email,
      comissaoPercentual: f.comissaoPercentual != null ? String(f.comissaoPercentual) : '',
    });
    setMostrarModalEditar(true);
  };

  const handleCriar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuarioLogado) return;

    const estabelecimentoId = isAdmin
      ? createForm.estabelecimentoId ? parseInt(createForm.estabelecimentoId) : null
      : (usuarioLogado.estabelecimentoId ?? null);

    const comissaoPercentual = isGerente && createForm.comissaoPercentual !== ''
      ? parseFloat(createForm.comissaoPercentual)
      : null;

    cadastrar(
      {
        nome: createForm.nome,
        email: createForm.email,
        senha: createForm.senha,
        tipo: createForm.tipo,
        estabelecimentoId,
        usuarioLogadoId: usuarioLogado.id,
        comissaoPercentual,
      },
      { onSuccess: () => setMostrarModalCriar(false) }
    );
  };

  const handleEditar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuarioLogado) return;

    const comissaoPercentual = isGerente && editForm.comissaoPercentual !== ''
      ? parseFloat(editForm.comissaoPercentual)
      : null;

    atualizar(
      { id: editForm.id, nome: editForm.nome, email: editForm.email, comissaoPercentual },
      { onSuccess: () => setMostrarModalEditar(false) }
    );
  };

  const tiposParaCriar = isAdmin
    ? [TipoUsuario.Administrador, TipoUsuario.GerenteEstabelecimento, TipoUsuario.FuncionarioEstabelecimento]
    : [TipoUsuario.FuncionarioEstabelecimento];

  return (
    <div className="animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl font-display font-bold text-neutral-900">Funcionários</h1>
          <p className="text-neutral-500 text-sm mt-1">
            {isAdmin ? 'Gerencie os funcionários de todos os estabelecimentos' : 'Gerencie os funcionários do seu estabelecimento'}
          </p>
        </div>
        <Button onClick={abrirModalCriar} variant="primary" size="md">
          Novo Funcionário
        </Button>
      </div>

      {isAdmin && (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-4 mb-6 flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Filtrar por nome..."
            value={filtroNome}
            onChange={(e) => setFiltroNome(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 flex-1 min-w-48"
          />
          <input
            type="text"
            placeholder="Filtrar por email..."
            value={filtroEmail}
            onChange={(e) => setFiltroEmail(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 flex-1 min-w-48"
          />
          <select
            value={filtroEstabelecimentoId}
            onChange={(e) => setFiltroEstabelecimentoId(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 flex-1 min-w-48"
          >
            <option value="">Todos os estabelecimentos</option>
            {estabelecimentos?.map((est) => (
              <option key={est.id} value={est.id}>{est.nome}</option>
            ))}
          </select>
        </div>
      )}

      {isLoading ? (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-6">
          <p className="text-neutral-500 text-center py-8">Carregando funcionários...</p>
        </div>
      ) : funcionarios && funcionarios.length > 0 ? (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-md overflow-hidden">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase hidden sm:table-cell">Email</th>
                {isAdmin && <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase hidden sm:table-cell">Tipo</th>}
                {isAdmin && <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase hidden sm:table-cell">Estabelecimento</th>}
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {funcionarios.map((f) => {
                const nomeEstabelecimento = f.estabelecimentoId
                  ? estabelecimentos?.find((e) => e.id === f.estabelecimentoId)?.nome ?? `#${f.estabelecimentoId}`
                  : '-';

                return (
                  <tr key={f.id} className={!f.ativo ? 'bg-neutral-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{f.nome}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 hidden sm:table-cell">{f.email}</td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 hidden sm:table-cell">
                        {tipoLabels[f.tipo as TipoUsuario] ?? f.tipo}
                      </td>
                    )}
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 hidden sm:table-cell">{nomeEstabelecimento}</td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        f.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {f.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                      {f.id !== usuarioLogado?.id && (
                        <>
                          <button
                            onClick={() => abrirModalEditar(f)}
                            className="text-primary-600 hover:text-blue-900"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => alternarStatus(f.id)}
                            className={f.ativo ? 'text-danger-600 hover:text-red-900' : 'text-success-600 hover:text-green-900'}
                          >
                            {f.ativo ? 'Desativar' : 'Ativar'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-6">
          <p className="text-neutral-500 text-center py-8">Nenhum funcionário encontrado</p>
        </div>
      )}

      {/* Modal Criar */}
      {mostrarModalCriar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-neutral-100 shadow-md-xl p-6 w-full max-w-md">
            <h2 className="text-2xl font-display font-bold text-neutral-900 mb-6">Novo Funcionário</h2>
            <form onSubmit={handleCriar} className="space-y-5">
              <Input
                label="Nome *"
                type="text"
                value={createForm.nome}
                onChange={(e) => setCreateForm({ ...createForm, nome: e.target.value })}
                onValidate={validateRequired('Nome')}
                validateOnBlur
              />

              <Input
                label="Email *"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                onValidate={combineValidations(validateRequired('Email'), validateEmail)}
                validateOnBlur
              />

              <Input
                label="Senha *"
                type="password"
                value={createForm.senha}
                onChange={(e) => setCreateForm({ ...createForm, senha: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                onValidate={combineValidations(validateRequired('Senha'), validateMinLength(6, 'Senha'))}
                validateOnBlur
                helperText="Mínimo 6 caracteres"
              />

              {isAdmin && (
                <Select
                  label="Tipo"
                  value={createForm.tipo}
                  onChange={(e) => setCreateForm({ ...createForm, tipo: e.target.value as TipoUsuario })}
                >
                  {tiposParaCriar.map((tipo) => (
                    <option key={tipo} value={tipo}>{tipoLabels[tipo]}</option>
                  ))}
                </Select>
              )}

              {isAdmin && (
                <Select
                  label="Estabelecimento"
                  value={createForm.estabelecimentoId}
                  onChange={(e) => setCreateForm({ ...createForm, estabelecimentoId: e.target.value })}
                  onValidate={(value) => {
                    if (createForm.tipo !== TipoUsuario.Administrador && !value) {
                      return 'Selecione um estabelecimento';
                    }
                    return undefined;
                  }}
                  validateOnBlur
                >
                  <option value="">Selecione...</option>
                  {estabelecimentos?.map((est) => (
                    <option key={est.id} value={est.id}>{est.nome}</option>
                  ))}
                </Select>
              )}

              {isGerente && (
                <Input
                  label="Comissão (%)"
                  type="number"
                  value={createForm.comissaoPercentual}
                  onChange={(e) => setCreateForm({ ...createForm, comissaoPercentual: e.target.value })}
                  placeholder="Ex: 5"
                  helperText="Percentual sobre o valor das vendas (opcional)"
                />
              )}

              <div className="flex gap-3 pt-4 border-t border-neutral-100">
                <Button type="submit" variant="primary" size="md" fullWidth disabled={cadastrando}>
                  {cadastrando ? 'Salvando...' : 'Cadastrar'}
                </Button>
                <Button type="button" onClick={() => setMostrarModalCriar(false)} variant="secondary" size="md" fullWidth disabled={cadastrando}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {mostrarModalEditar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-neutral-100 shadow-md-xl p-6 w-full max-w-md">
            <h2 className="text-2xl font-display font-bold text-neutral-900 mb-6">Editar Funcionário</h2>
            <form onSubmit={handleEditar} className="space-y-5">
              <Input
                label="Nome *"
                type="text"
                value={editForm.nome}
                onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                onValidate={validateRequired('Nome')}
                validateOnBlur
              />

              <Input
                label="Email *"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                onValidate={combineValidations(validateRequired('Email'), validateEmail)}
                validateOnBlur
              />

              {isGerente && (
                <Input
                  label="Comissão (%)"
                  type="number"
                  value={editForm.comissaoPercentual}
                  onChange={(e) => setEditForm({ ...editForm, comissaoPercentual: e.target.value })}
                  placeholder="Ex: 5"
                  helperText="Percentual sobre o valor das vendas (opcional)"
                />
              )}

              <div className="flex gap-3 pt-4 border-t border-neutral-100">
                <Button type="submit" variant="primary" size="md" fullWidth disabled={atualizando}>
                  {atualizando ? 'Salvando...' : 'Salvar'}
                </Button>
                <Button type="button" onClick={() => setMostrarModalEditar(false)} variant="secondary" size="md" fullWidth disabled={atualizando}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Funcionarios;
